import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { clientAPI } from "@/services/api";
import { getClientOnboardingDraftKey } from "@/lib/proveRequestForm";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

const ClientPaymentReturn = ({ onComplete, clientId }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reference = params.get("reference") || params.get("trxref");
  const activeReferenceRef = useRef(reference);
  const [status, setStatus] = useState("pending");
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState("");
  const [checkVersion, setCheckVersion] = useState(0);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState(
    "Waiting for Paystack's signed webhook confirmation…"
  );

  useEffect(() => {
    activeReferenceRef.current = reference;
    if (!reference) {
      setStatus("failed");
      setMessage("No payment reference was supplied.");
      return () => {
        if (activeReferenceRef.current === reference) activeReferenceRef.current = null;
      };
    }

    let stopped = false;
    let attempts = 0;
    let timeoutId;
    const isCurrent = () => !stopped && activeReferenceRef.current === reference;

    setStatus("pending");
    setMessage("Waiting for Paystack's signed webhook confirmation…");
    setCompletionError("");

    const poll = async () => {
      if (!isCurrent()) return;
      timeoutId = undefined;
      attempts += 1;
      setChecking(true);
      try {
        const response = await clientAPI.getPaymentIntent(reference);
        // `clientAPI` normally returns Axios responses, while lightweight
        // adapters and integration tests may return the payload directly.
        const payload = response?.data ?? response;
        const nextStatus = payload?.status || "pending";
        if (!isCurrent()) return;
        setStatus(nextStatus);
        if (nextStatus === "paid") {
          setMessage("Escrow funding is confirmed and settled.");
          return;
        }
        if (["failed", "expired"].includes(nextStatus)) {
          setMessage(`Payment ${nextStatus}. Start a new funding attempt.`);
          return;
        }
      } catch (error) {
        if (!isCurrent()) return;
        if (error?.response?.status === 404) {
          setStatus("failed");
          setMessage("This payment reference does not belong to your account.");
          return;
        }
      } finally {
        if (isCurrent()) setChecking(false);
      }
      if (!isCurrent()) return;
      if (attempts < 60) {
        timeoutId = window.setTimeout(poll, 2000);
      }
      if (attempts >= 60 && isCurrent()) {
        setMessage("Confirmation is taking longer than expected. You can safely check again later.");
      }
    };
    poll();
    return () => {
      stopped = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (activeReferenceRef.current === reference) activeReferenceRef.current = null;
    };
  }, [reference, checkVersion]);

  const handleComplete = async () => {
    if (status !== "paid" || completing) return;
    setCompleting(true);
    setCompletionError("");
    try {
      await clientAPI.completeOnboarding();
      const resolvedClientId = clientId || localStorage.getItem("user_id") || "anonymous";
      localStorage.removeItem(getClientOnboardingDraftKey(resolvedClientId));
      onComplete?.();
      navigate("/client/dashboard");
    } catch (error) {
      setCompletionError(error.response?.data?.detail || "We couldn't complete onboarding yet. Try again shortly.");
    } finally {
      setCompleting(false);
    }
  };

  const Icon =
    status === "paid" ? CheckCircle2 : status === "pending" ? Clock : XCircle;

  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      <Icon
        className={`mx-auto h-12 w-12 ${
          status === "paid"
            ? "text-emerald-600"
            : status === "pending"
              ? "animate-pulse text-amber-600"
              : "text-rose-600"
        }`}
      />
      <h1 className="mt-4 text-xl font-semibold">Escrow payment</h1>
      <p role="status" aria-live="polite" className="mt-2 text-sm text-gray-600">{message}</p>
      <p className="mt-3 break-all font-mono text-xs text-gray-400">{reference}</p>
      {status === "paid" ? (
        <div className="mt-6 space-y-4 text-left">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Step 7 of 7</p>
            <p className="mt-1 text-base font-semibold text-emerald-950">Escrow funded</p>
            <p className="mt-1 text-sm text-emerald-800">Your offer and campaign are already created and ready for the dashboard.</p>
          </div>
          {completionError ? <p role="alert" className="text-sm text-rose-600">{completionError}</p> : null}
          <Button className="h-11 w-full" onClick={handleComplete} disabled={completing}>
            {completing ? "Completing onboarding…" : "Complete onboarding"}
          </Button>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {reference && status === "pending" ? (
            <Button variant="outline" disabled={checking} onClick={() => setCheckVersion((value) => value + 1)}>
              {checking ? "Checking payment status…" : "Check payment status again"}
            </Button>
          ) : null}
          <Button onClick={() => navigate("/client/onboarding")}>
            {status === "failed" ? "Start a new funding attempt" : "Return to onboarding"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClientPaymentReturn;
